import { useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Input, Select } from "../../../components/ui";
import { useCentinelaTestCall } from "../../../features/centinela/mutations";
import { TEST_CALL_KEYWORDS } from "../services/centinelaMocks";
import {
  buildTestCallInput,
  testCallErrorMessage,
  toE164,
} from "../services/centinelaTestCall";
import { CALL_SEVERITY_TONES, CALL_TYPE_ICONS, CALL_TYPE_SEVERITY } from "../theme";
import { CALL_EVENT_TYPES, type CallEventType } from "../types";

const COUNTRY_CODES = [
  { id: "mx", code: "+52", flag: "🇲🇽" },
  { id: "us", code: "+1", flag: "🇺🇸" },
  { id: "co", code: "+57", flag: "🇨🇴" },
  { id: "pe", code: "+51", flag: "🇵🇪" },
  { id: "ar", code: "+54", flag: "🇦🇷" },
  { id: "cl", code: "+56", flag: "🇨🇱" },
  { id: "es", code: "+34", flag: "🇪🇸" },
] as const;

type CallStatus = "idle" | "calling" | "success" | "error";

export interface TestCallModalProps {
  onClose: () => void;
}

export default function TestCallModal({ onClose }: TestCallModalProps) {
  const { t } = useTranslation("common");

  const [callType, setCallType] = useState<CallEventType | "">("");
  const [countryCode, setCountryCode] = useState<string>(COUNTRY_CODES[0].code);
  const [phone, setPhone] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [status, setStatus] = useState<CallStatus>("idle");
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState("");

  const testCall = useCentinelaTestCall();

  const isCalling = status === "calling";
  const selectedCountry = COUNTRY_CODES.find((country) => country.code === countryCode);

  const start = async () => {
    const e164 = toE164(countryCode, phone);
    if (!e164) {
      setStatus("idle");
      setFormError(t("monitoreoIa.testCall.invalidPhone"));
      return;
    }
    if (!callType) {
      setStatus("idle");
      setFormError(t("monitoreoIa.testCall.missingType"));
      return;
    }

    setStatus("calling");
    setFormError("");
    setMessage("");

    try {
      await testCall.mutateAsync(
        buildTestCallInput({
          type: callType,
          phone: e164,
          name: operatorName.trim() || t("monitoreoIa.testCall.defaultOperator"),
        }),
      );
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setMessage(testCallErrorMessage(error) || t("monitoreoIa.testCall.genericError"));
    }
  };

  const reset = () => {
    setStatus("idle");
    setMessage("");
    setFormError("");
    setCallType("");
    setPhone("");
    setOperatorName("");
  };

  return (
    <Modal
      title={t("monitoreoIa.testCall.title")}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <div className="space-y-5">
          <p className="mx-auto max-w-md text-center text-[13px] leading-relaxed text-content-secondary">
            {t("monitoreoIa.testCall.intro")}
          </p>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CALL_EVENT_TYPES.map((type) => {
              const isSelected = callType === type;
              const severity = CALL_TYPE_SEVERITY[type];

              return (
                <button
                  key={type}
                  type="button"
                  disabled={isCalling}
                  onClick={() => {
                    setCallType(type);
                    setStatus("idle");
                    setMessage("");
                  }}
                  className={`relative flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    isSelected
                      ? "border-primary-500 bg-primary-500/10"
                      : "border-line bg-app hover:border-primary-500/40 hover:bg-subtle"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? "bg-primary-500/15 text-primary-500"
                        : "bg-subtle text-content-muted"
                    }`}
                  >
                    <i className={`${CALL_TYPE_ICONS[type]} text-sm`} />
                  </span>

                  <span className="text-[13px] font-bold leading-tight text-content">
                    {t(`monitoreoIa.callTypes.${type}`)}
                  </span>

                  <span className="flex-1 text-[11px] leading-relaxed text-content-secondary">
                    {t(`monitoreoIa.callTypeHints.${type}`)}
                  </span>

                  <Badge tone={CALL_SEVERITY_TONES[severity]} size="sm">
                    {t(`monitoreoIa.testCall.severity.${severity}`)}
                  </Badge>

                  {isSelected && (
                    <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
                      <i className="ri-check-line text-[10px] text-on-primary" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="rounded-xl border border-line bg-surface p-5">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-content">
              <i className="ri-phone-line text-sm" />
              {t("monitoreoIa.testCall.receiveTitle")}
            </h3>

            {status === "idle" && (
              <div className="space-y-4">
                <div className="grid gap-3 lg:grid-cols-3">
                  <Select
                    label={t("monitoreoIa.testCall.countryLabel")}
                    value={countryCode}
                    onChange={(event) => setCountryCode(event.target.value)}
                    options={COUNTRY_CODES.map((country) => ({
                      value: country.code,
                      label: `${country.flag} ${country.code} · ${t(`monitoreoIa.testCall.countries.${country.id}`)}`,
                    }))}
                  />
                  <Input
                    label={t("monitoreoIa.testCall.phoneLabel")}
                    required
                    value={phone}
                    inputMode="tel"
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="222 123 4567"
                    icon={<span className="text-sm">{selectedCountry?.flag}</span>}
                    containerClassName="lg:col-span-2"
                  />
                </div>

                <Input
                  label={t("monitoreoIa.testCall.nameLabel")}
                  value={operatorName}
                  onChange={(event) => setOperatorName(event.target.value)}
                  placeholder={t("monitoreoIa.testCall.namePlaceholder")}
                />

                <p className="rounded-xl border border-warning/30 bg-warning/10 p-3.5 text-xs leading-relaxed text-content-secondary">
                  <i className="ri-key-2-line mr-1.5 text-warning" />
                  {t("monitoreoIa.testCall.keywordNotice", {
                    word: TEST_CALL_KEYWORDS.word,
                    duress: TEST_CALL_KEYWORDS.duress,
                  })}
                </p>

                {formError ? (
                  <p className="rounded-xl border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">
                    {formError}
                  </p>
                ) : null}

                <Button
                  fullWidth
                  disabled={!callType || phone.trim().length === 0}
                  onClick={start}
                  icon={<i className="ri-phone-line" />}
                >
                  {t("monitoreoIa.testCall.callNow")}
                </Button>
              </div>
            )}

            {isCalling && (
              <div className="flex flex-col items-center gap-4 py-6">
                <span className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full border border-primary-500/30 bg-primary-500/10">
                  <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
                </span>
                <div className="text-center">
                  <p className="text-[15px] font-bold text-content">
                    {t("monitoreoIa.testCall.dialing")}
                  </p>
                  {callType && (
                    <p className="mt-1 text-[13px] text-content-secondary">
                      {t(`monitoreoIa.callTypes.${callType}`)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {status === "success" && (
              <div className="flex flex-col items-center gap-4 py-6">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-success/30 bg-success/10">
                  <i className="ri-phone-line text-xl text-success" />
                </span>
                <div className="max-w-sm text-center">
                  <p className="text-[15px] font-bold text-content">
                    {t("monitoreoIa.testCall.successTitle")}
                  </p>
                  <p className="mt-1 text-[13px] text-content-secondary">
                    {t("monitoreoIa.testCall.successReal")}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button variant="outline" onClick={reset}>
                    {t("monitoreoIa.testCall.tryAnother")}
                  </Button>
                  <Button onClick={onClose}>{t("monitoreoIa.testCall.close")}</Button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center gap-4 py-6">
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-error/30 bg-error/10">
                  <i className="ri-error-warning-line text-xl text-error" />
                </span>
                <p className="max-w-sm text-center text-sm font-semibold text-error">{message}</p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button variant="outline" onClick={reset}>
                    {t("monitoreoIa.testCall.tryAnother")}
                  </Button>
                  <Button onClick={start}>{t("monitoreoIa.testCall.retry")}</Button>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-[11px] text-content-faint">
            {t("monitoreoIa.testCall.footerNote")}
          </p>
        </div>
      }
    />
  );
}
